/*

BOUNDING BOXES ARE A PROBLEM

A) THEY ARE DERIVED DATA
B) THEY HAVE TO BE USED IN SOME ACTIONS

We cannot store derived data in the model, so we will use a manager-selector.

A manager-selector is a piece of code which retrieves derived data from the model, using cache memoization, just like view selectors.

*/

import threeShim from '../../shim/three';
import simmut from 'simmut';

const { Vector3, Matrix4 } = threeShim;

const { get } = simmut;

const BIG_NUMBER = 1e20;
const SMALL_NUMBER = -1e20;
// If sync time is longer than 20 ms, we report that
const TIME_FOR_REPORTING = 20;

const boundingBoxHelperFactory = () => {
    let _rootRef;

    let _rootBBox;

    const _topLevelObjectBoxes = {};

    const _mergeBox = (box1, box2) => {
        if (!box1) {
            return box2;
        }
        if (!box2) {
            return box1;
        }
        const resultBox = {
            min: {
                x: Math.min(box1.min.x, box2.min.x),
                y: Math.min(box1.min.y, box2.min.y),
            },
            max: {
                x: Math.max(box1.max.x, box2.max.x),
                y: Math.max(box1.max.y, box2.max.y),
            },
        };

        return resultBox;
    };

    const _getBoundingBoxFromVertices = (vertices, matrixToUse) => {
        const min = {
            x: BIG_NUMBER,
            y: BIG_NUMBER,
        };
        const max = {
            x: SMALL_NUMBER,
            y: SMALL_NUMBER,
        };

        const p3d = new Vector3();

        for (let i = 0; i < vertices.length; i += 3) {
            p3d.set(vertices[i], vertices[i + 1], vertices[i + 2]);
            p3d.applyMatrix4(matrixToUse);
            if (p3d.x < min.x) {
                min.x = p3d.x;
            }
            if (p3d.y < min.y) {
                min.y = p3d.y;
            }
            if (p3d.x > max.x) {
                max.x = p3d.x;
            }
            if (p3d.y > max.y) {
                max.y = p3d.y;
            }
        }
        return {
            min,
            max,
        };
    };

    const computeBoundingBoxRecursive = (obj, matrix) => {
        if (!obj) {
            return null;
        }
        let matrixToUse = matrix || new Matrix4();

        let boundingBox = {
            min: {
                x: BIG_NUMBER,
                y: BIG_NUMBER,
            },
            max: {
                x: SMALL_NUMBER,
                y: SMALL_NUMBER,
            },
        };

        const _reviewProperties = (container) => {
            const properties = container.properties;

            if (properties) {
                const {
                    x,
                    y,
                    sx = 0,
                    sy = 0,
                    width,
                    height,
                    angle,
                } = properties;

                if (x !== undefined && y !== undefined) {
                    let theta = angle;

                    if (theta) {
                        theta = (theta * Math.PI) / 180;
                    }
                    const objMatrix = new Matrix4();

                    objMatrix.makeRotationZ(theta || 0);
                    objMatrix.setPosition(new Vector3(x, y, 0));
                    matrixToUse = matrixToUse.clone().multiply(objMatrix);
                }
                if (width !== undefined && height !== undefined) {
                    // We add the vertices of the rectangle
                    const vertices = [
                        sx - width * 0.5,
                        sy - height * 0.5,
                        0,
                        sx + width * 0.5,
                        sy - height * 0.5,
                        0,
                        sx + width * 0.5,
                        sy + height * 0.5,
                        0,
                        sx - width * 0.5,
                        sy + height * 0.5,
                        0,
                    ];

                    const box = _getBoundingBoxFromVertices(
                        vertices,
                        matrixToUse,
                    );

                    return box;
                }
            }
            return null;
        };

        const _loopChairs = (container) => {
            const chairs = container.chairs;

            if (chairs) {
                const { x = [], y = [], d = [] } = chairs;
                const vertices = [];

                for (let i = 0; i < x.length; i++) {
                    // If chair not deleted
                    if (!d[i]) {
                        vertices.push(x[i], y[i], 0);
                    }
                }

                const chairsBoundingBox = _getBoundingBoxFromVertices(
                    vertices,
                    matrixToUse,
                );

                return chairsBoundingBox;
            }
            return null;
        };

        const _loopContents = (container) => {
            const contents = container.contents;

            let totalBox;

            if (contents) {
                const children = Object.keys(contents) || [];

                children.forEach((childName) => {
                    const child = contents[childName];

                    if (child) {
                        const childBox = computeBoundingBoxRecursive(
                            child,
                            matrixToUse,
                        );

                        totalBox = _mergeBox(totalBox, childBox);
                    }
                });
            }
            return totalBox;
        };

        const ownBox = _reviewProperties(obj);
        const chairsBox = _loopChairs(obj);
        const childrenBox = _loopContents(obj);

        if (ownBox) {
            boundingBox = _mergeBox(boundingBox, ownBox);
        }
        if (chairsBox) {
            boundingBox = _mergeBox(boundingBox, chairsBox);
        }
        if (childrenBox) {
            boundingBox = _mergeBox(boundingBox, childrenBox);
        }
        return boundingBox;
    };

    const _syncBoundingBox = ({ node }) => {
        if (!node || !node.contents) {
            return null;
        }
        const contents = node.contents;
        const childNames = Object.keys(contents);
        const childNameMap = {};

        childNames.forEach((childName) => {
            const child = contents[childName];

            childNameMap[childName] = true;
            let _cacheObject = _topLevelObjectBoxes[childName];

            if (!_cacheObject) {
                _cacheObject = {};
                _topLevelObjectBoxes[childName] = _cacheObject;
            }
            if (_cacheObject.ref !== child) {
                _cacheObject.ref = child;
                _cacheObject.bbox = computeBoundingBoxRecursive(child);
            }
        });
        // We remove the objects which are in _topLevelObjectBoxes (from a previous sync),
        // but have been removed from the children
        Object.keys(_topLevelObjectBoxes).forEach((topLevelName) => {
            if (!childNameMap[topLevelName]) {
                delete _topLevelObjectBoxes[topLevelName];
            }
        });

        const bbox = Object.keys(_topLevelObjectBoxes).reduce(
            (accumBox, key) => {
                const childBox = _topLevelObjectBoxes[key].bbox;

                return _mergeBox(accumBox, childBox);
            },
            null,
        );

        return bbox;
    };

    // Calculates the bounding box from the top object
    // It relies on _hash caching for fast updates
    const getBoundingBox = ({ node }) => {
        if (!node) {
            return null;
        }
        const objectsNode = get(node, 'objects');

        if (!objectsNode) {
            return null;
        }
        const t1 = Date.now();

        if (objectsNode !== _rootRef || !_rootBBox) {
            _rootBBox = _syncBoundingBox({ node: objectsNode });
            _rootRef = objectsNode;
        }
        const t2 = Date.now();

        if (t2 - t1 > TIME_FOR_REPORTING) {
            console.log(`Bounding box calculation took ${t2 - t1}`);
        }
        return _rootBBox;
    };

    return {
        // Retrieves boundingBox from cache or updates the boundingBox
        // looking at the sections which have changed
        getBoundingBox,
        // Performs actual calculation of a boundingBox from a starting object
        // Please use with care as this can be potentially slow
        computeBoundingBoxRecursive,
    };
};

export default boundingBoxHelperFactory;
